<template>
  <div
    ref="inlineError"
    :class="[
      snackbar.type,
      {
        'inline-error': true,
        'inline-error--visible': snackbar.show,
        'inline-error--hide': !snackbar.show,
      }
    ]"
    :style="snackbarStyles"
  >
    <div class="inline-error__message muted-immutable--text">
      {{ snackbar.text }}
    </div>

    <v-icon
      v-if="showCloseBtn && snackbar.show"
      class="inline-error__close"
      color="muted-immutable"
      @click="close"
    >
      close
    </v-icon>
  </div>
</template>

<script>
import Formatters from '@/shared/formatters/formatters';
export default {
  name: 'InlineError',
  props: {
    top: {
      type: [Number, String],
      default: null,
    },
  },
  data: () => ({
    snackbar: {
      show: false,
      type: '',
      text: '',
      timeout: 10000,
    },
    internalTop: null,
    dynamicTop: null,
    initialScrollY: 0,
    timeoutId: null,
    closeBtnDelayId: null,
    showCloseBtn: false,
    hideTimeoutId: null,
  }),
  computed: {
    snackbarStyles() {
      const style = {
        minHeight: '50px',
      };

      const topValue = this.computedTop;
      if (topValue !== null) {
        style.top = topValue;
      }

      return style;
    },
    computedTop() {
      if (!this.formatter.isEmptyValue(this.dynamicTop)) {
        return this.formatTop(this.dynamicTop);
      }

      if (!this.formatter.isEmptyValue(this.internalTop)) {
        return this.formatTop(this.internalTop);
      }

      if (!this.formatter.isEmptyValue(this.top)) {
        return this.formatTop(this.top);
      }

      return null;
    },
  },
  created() {
    this.formatter = new Formatters();
  },
  mounted() {
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', this.handleScroll, { passive: true });
    }
  },
  beforeDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('scroll', this.handleScroll);
    }

    if (this.timeoutId) clearTimeout(this.timeoutId);
    if (this.closeBtnDelayId) clearTimeout(this.closeBtnDelayId);
    if (this.hideTimeoutId) clearTimeout(this.hideTimeoutId);
  },
  methods: {
    formatTop(value) {
      if (typeof value === 'number') return `${value}px`;

      const numeric = Number(value);
      if (!Number.isNaN(numeric) && `${numeric}` === `${value}`) {
        return `${numeric}px`;
      }
      return value;
    },

    open(type, text, timeout = 10000, top = null) {
      this.snackbar.type = type;
      this.snackbar.text = text;
      this.snackbar.timeout = timeout;
      this.internalTop = top;
      this.dynamicTop = null;
      this.initialScrollY = this.getScrollPosition();

      if (this.hideTimeoutId) {
        clearTimeout(this.hideTimeoutId);
        this.hideTimeoutId = null;
      }

      this.snackbar.show = true;
      this.handleScroll();

      this.showCloseBtn = false;
      if (this.closeBtnDelayId) clearTimeout(this.closeBtnDelayId);

      this.closeBtnDelayId = setTimeout(() => {
        this.showCloseBtn = true;
      }, 800);

      if (this.timeoutId) clearTimeout(this.timeoutId);

      if (timeout && timeout > 0) {
        this.timeoutId = setTimeout(() => this.close(), timeout);
      }
    },

    close() {
      this.snackbar.show = false;
      this.showCloseBtn = false;
      this.dynamicTop = null;

      if (this.timeoutId) clearTimeout(this.timeoutId);
      if (this.closeBtnDelayId) clearTimeout(this.closeBtnDelayId);
      if (this.hideTimeoutId) clearTimeout(this.hideTimeoutId);

      this.timeoutId = null;
      this.closeBtnDelayId = null;

      this.hideTimeoutId = setTimeout(() => {
        this.hideTimeoutId = null;
      }, 700);
    },

    handleScroll() {
      if (!this.snackbar.show) return;

      const baseTop = !this.formatter.isEmptyValue(this.internalTop)
        ? this.internalTop
        : this.top;

      const numericBaseTop = this.parseTopValue(baseTop);
      if (numericBaseTop === null) {
        this.dynamicTop = null;
        return;
      }

      const currentScroll = this.getScrollPosition();
      const delta = currentScroll - this.initialScrollY;
      this.dynamicTop = numericBaseTop - delta;
    },

    getScrollPosition() {
      if (typeof window === 'undefined') return 0;
      return window.scrollY || window.pageYOffset || 0;
    },

    parseTopValue(value) {
      if (this.formatter.isEmptyValue(value)) return null;

      if (typeof value === 'number') return value;

      if (typeof value === 'string') {
        const trimmed = value.trim();
        let normalized = trimmed;

        if (trimmed.toLowerCase().endsWith('px')) {
          normalized = trimmed.slice(0, -2);
        }

        const numeric = Number(normalized);
        if (!Number.isNaN(numeric)) {
          return numeric;
        }
      }

      return null;
    },
  },
};
</script>

<style lang="scss">
.inline-error {
  position: fixed;
  right: 0;
  top: 0;
  z-index: 9999;
  width: 0;
  margin: 0 20px;
  overflow: hidden;
  transition: width .7s ease, opacity .7s ease;
  opacity: 0;
  pointer-events: none;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0 16px;
  border-radius: 4px;

  box-shadow: 0 8px 10px color-mix(in srgb, var(--v-shadow-base) 12%, transparent),
              0 3px 14px color-mix(in srgb, var(--v-shadow-base) 12%, transparent),
              0 5px 5px color-mix(in srgb, var(--v-shadow-base) 2%, transparent);

  &--visible {
    width: calc(100% - 40px);
    opacity: 1;
    pointer-events: auto;
  }

  &--hide {
    width: 0;
    opacity: 0;
    pointer-events: none;
  }
}

.inline-error__message {
  width: 100%;
  text-align: start;
  font-weight: 500;
}

.inline-error__close {
  margin-left: 8px;
  cursor: pointer;
}
</style>
